/* ══════════════════════════════════════════════════════════════════════
   GRAPH CONTROLS — the chrome both PAG graphs wear.

   Policy Insights and Network Graph are two views of one dataset, so they
   should not have two different sets of buttons in two different places
   doing the same four things. This file is the one set: the same button,
   the same corners, the same fullscreen behaviour.

   It lives beside NetworkLegend, which both graphs already import for the
   same reason.
   ══════════════════════════════════════════════════════════════════════ */
import * as React from "react";
import { Expand, Maximize2, Minimize2, Minus, Plus } from "lucide-react";
import { SURFACE, type NetSurface } from "./networkGraphVisuals";

/* ── Fullscreen ─────────────────────────────────────────────────────────
   The real Fullscreen API, not a `fixed inset-0` overlay.

   The overlay is what the Network Graph used, and it did not work: a
   fixed element is positioned against the nearest ancestor that creates a
   containing block, and this page wraps both graphs in an animated
   motion.div. So "fullscreen" filled that box rather than the screen.
   requestFullscreen escapes the document entirely and cannot be clipped
   by anything above it.

   The `fullscreenchange` subscription is the other half, and the half
   Policy Insights was missing: it called requestFullscreen but never
   listened, so its own isFullscreen flag stayed false forever and the
   button kept offering "Fullscreen" while already in it. Browser chrome
   (Escape, F11) changes this state without asking us, so the event is the
   only honest source for it. */
export function useGraphFullscreen(ref: React.RefObject<HTMLElement | null>) {
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  React.useEffect(() => {
    const sync = () => setIsFullscreen(document.fullscreenElement === ref.current);
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, [ref]);

  const toggle = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen?.();
    else void el.requestFullscreen?.();
  }, [ref]);

  return { isFullscreen, toggle };
}

/* ── Button ─────────────────────────────────────────────────────────── */

/** Resolves on hover, but legible before it. The previous build sat at
 *  `muted` on a translucent panel, which on the dark canvas left the zoom
 *  cluster almost invisible until you knew to look for it. */
export function CtrlButton({
  label,
  onClick,
  isDark,
  children,
}: {
  label: string;
  onClick: () => void;
  isDark: boolean;
  children: React.ReactNode;
}) {
  const surface: NetSurface = isDark ? SURFACE.dark : SURFACE.light;
  const [hover, setHover] = React.useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="flex size-7 items-center justify-center rounded-md transition-colors"
      style={{
        border: `1px solid ${hover ? "#818CF8" : surface.panelBorder}`,
        /* Opaque, not the translucent legend panel: these sit over the
           graph itself, and a see-through button competes with whatever
           edge happens to run under it. */
        background: isDark ? "#151E31" : "#FFFFFF",
        color: hover ? "#818CF8" : surface.label,
        boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.45)" : "0 1px 3px rgba(15,23,42,0.12)",
      }}
    >
      {children}
    </button>
  );
}

const ICON = "size-4";

/* ── Clusters ───────────────────────────────────────────────────────────
   Placement is part of the contract, not the caller's choice: fullscreen
   top-left, zoom bottom-left, on both graphs. */

export function GraphFullscreenControl({
  isFullscreen,
  onToggle,
  isDark,
}: {
  isFullscreen: boolean;
  onToggle: () => void;
  isDark: boolean;
}) {
  return (
    <div className="absolute left-3 top-3 z-10">
      <CtrlButton
        label={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
        onClick={onToggle}
        isDark={isDark}
      >
        {isFullscreen ? <Minimize2 className={ICON} /> : <Maximize2 className={ICON} />}
      </CtrlButton>
    </div>
  );
}

export function GraphZoomControls({
  onZoomIn,
  onZoomOut,
  onFit,
  isDark,
}: {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFit: () => void;
  isDark: boolean;
}) {
  return (
    <div className="absolute bottom-3 left-3 z-10 flex flex-col gap-1.5">
      <CtrlButton label="Zoom In" onClick={onZoomIn} isDark={isDark}>
        <Plus className={ICON} />
      </CtrlButton>
      <CtrlButton label="Zoom Out" onClick={onZoomOut} isDark={isDark}>
        <Minus className={ICON} />
      </CtrlButton>
      <CtrlButton label="Fit to Screen" onClick={onFit} isDark={isDark}>
        <Expand className={ICON} />
      </CtrlButton>
    </div>
  );
}
